/**
 * The body of one news article page (`/about-us/news-events/news/<categorySlug>/<slug>`): title,
 * category badge and subtitle, the Contentful rich-text body, a "Latest News" sidebar with the
 * three newest articles, and the newsletter sign-up. Also exports the category label and colour
 * lookups that every news list uses.
 *
 * Rendered only by `./NewsArticleContainer.tsx`, which sets the meta tags. The article comes from
 * `src/contentful/news/getContentfulNewsArticleDetails.ts`, the sidebar list from
 * `getContentfulNewsForSections.ts`; the body renders through
 * `components/Shared/model/Contentful.tsx` → `renderRichText`.
 *
 * Rules a change must keep: the keys of `MultiLingualNewsCategories` are the article URL category
 * slugs. They must match `src/contentful/news/helpers/index.ts` →
 * `getNewsCategorySlugFromCategoryLabel` and the `categories[].url` values in
 * `src/content/ryerson/about-us/news-events/news/index.ts`.
 *
 * As the code stands:
 * - The breadcrumb is a fixed English list (Home › About Us › News & Events › News). Built from
 *   the URL it would include the category segment, which has no page of its own.
 * - The sidebar's three articles can include the one being read, and their links aren't
 *   `encodeURI`d, unlike the lists in `./AllNews.tsx` and `./LatestNewsSection.tsx`.
 * - The scroll handler that moves the vertical social bar is added on mount and never removed;
 *   `components/NewsEvents/Events/EventDetails.tsx` and `components/Blog/Blog.tsx` hold the same
 *   handler.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection } from "@ryerson/frontend.layout";
import { Badge } from "@ryerson/frontend.notification";
import { Link } from "@ryerson/frontend.navigation";
import { Breadcrumb, Crumb } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import SocialMedia from "@components/NewsEvents/SocialMedia/SocialMedia";
import styled from "@emotion/styled";
import { renderRichText } from "@components/Shared/model/Contentful";
import { css } from "@emotion/react";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import Spacer from "@components/Shared/Spacer/Spacer";
import {
	ProcessedContentfulNewsArticleAllFields,
	ProcessedContentfulNewsForSectionsByLanguage,
} from "@contentful/news/types";
import { StaticContent } from "../Content";

/**
 * English category labels with a badge colour in {@link NewsBadgeColor}. Callers cast the
 * Contentful `category` to it unchecked. "Ryerson Earnings" isn't one of the four labels
 * `src/contentful/news/helpers/index.ts` → `getNewsCategorySlugFromCategoryLabel` knows, and
 * "Earning Calls", which is, isn't listed.
 */
export type NewsCategories =
	| "Ryerson Earnings"
	| "Earnings Releases"
	| "Ryerson Initiatives"
	| "Ryerson Quarter Results";

/**
 * Translated category badge labels, by language and then by URL category slug. A slug missing
 * here renders an empty badge.
 */
export const MultiLingualNewsCategories: any = {
	en: {
		"ryerson-quarter-results": "Ryerson Quarter Results",
		"earning-calls": "Earning Calls",
		"earnings-releases": "Earnings Releases",
		"ryerson-initiatives": "Ryerson Initiatives",
	},
	fr: {
		"ryerson-quarter-results": "Résultats trimestriels de Ryerson",
		"earning-calls": "Gagner des appels",
		"earnings-releases": "Publications sur les résultats",
		"ryerson-initiatives": "Initiatives Ryerson",
	},
	es: {
		"ryerson-quarter-results": "Resultados del trimestre de Ryerson",
		"earning-calls": "Ganar llamadas",
		"earnings-releases": "Comunicados de resultados",
		"ryerson-initiatives": "Iniciativas Ryerson",
	},
};

/**
 * The badge colour for an English category label: green, blue, red or yellow for the four
 * listed in {@link NewsCategories}, purple for any other label (today that includes
 * "Earning Calls"). It calls `useTheme`, so it may only be called while a component renders.
 */
export const NewsBadgeColor = (category: NewsCategories) => {
	const { theme } = useTheme();
	const categoryAssociations = {
		"Ryerson Earnings": theme.colors.secondary.green,
		"Earnings Releases": theme.colors.secondary.blue,
		"Ryerson Initiatives": theme.colors.secondary.red,
		"Ryerson Quarter Results": theme.colors.secondary.yellow,
	};
	if (categoryAssociations[category]) return categoryAssociations[category];
	return theme.colors.secondary.purple;
};

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 0px;
			}
		`;
	}}
`;

const Divider = styled.hr`
	margin-top: 0px;
	margin-bottom: 0px;
	opacity: 0.1;
`;

const Left = styled.div`
	display: inline-block;
	width: calc(100% - 300px);
	vertical-align: top;
	padding: 27px 40px 96px 40px;
	box-sizing: border-box;
	height: auto;
	border-radius: 2px;
	margin-top: -310px;
	position: relative;
	margin-bottom: 60px;
	img {
		max-width: 100%;
	}
	p {
		max-width: 660px;
		margin-top: 15px;
		margin-bottom: 15px;
	}
`;

const Right = styled.div`
	display: inline-block;
	width: 290px;
	margin-top: -310px;
	vertical-align: top;
	margin-left: 10px;
`;

const LatestNewsContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
	padding: 20px 23px;
	box-sizing: border-box;
	margin-bottom: 10px;
	border-radius: 2px;
`;

const Social = styled.div`
	width: 20px;
	height: auto;
	left: -36px;
	position: absolute;
`;

// The GA `stay_tuned` event value is "News-Article_<categorySlug>" for sign-ups from an article
// (see `Shared/StayTuned/StayTuned.tsx`).
const stayTunedParent = "News-Article";

/** Props from `./NewsArticleContainer.tsx`. */
export type NewsArticleProps = {
	/** Every article, newest first, per language; the sidebar shows the first three. */
	contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
	/** The article being read, in the current language. */
	contentfulNewsArticleForLanguage: ProcessedContentfulNewsArticleAllFields;
};

/** Renders one article page body for desktop (`lg` and up) and mobile; see the file header. */
const NewsArticle: React.FC<NewsArticleProps> = ({
	contentfulNewsByLanguage,
	contentfulNewsArticleForLanguage,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	// "YYYY-MM-DD" -> "MM/DD/YYYY"; see `./LatestNewsSection.tsx` for the parsing caveat.
	const FormatDate = (date: any) => {
		let dateObject = new Date(date + " 00:00:00");
		let m: any = dateObject.getMonth() + 1;
		let d: any = dateObject.getDate();
		let y: any = dateObject.getFullYear();
		if (m < 10) m = "0" + m;
		if (d < 10) d = "0" + d;
		return m + "/" + d + "/" + y;
	};

	const [socialReference, setSocialReference] = React.useState<any>(React.createRef());
	const [socialTop, setSocialTop] = React.useState<number>(0);
	// Keeps the vertical social bar level with the viewport while the body (`Left`) scrolls past,
	// pinned at the top before it and near the bottom after it. The listener is never removed.
	React.useEffect(() => {
		if (window !== undefined && document !== undefined) {
			window.addEventListener("scroll", function () {
				if (socialReference.current) {
					let offset = socialReference.current.getBoundingClientRect();
					let startingTop = offset.top - document.body.getBoundingClientRect().top;
					if (
						window.scrollY + 120 >= startingTop &&
						window.scrollY + 265 < socialReference.current.offsetHeight + startingTop
					) {
						setSocialTop(window.scrollY - startingTop + 120);
					} else if (
						socialReference.current.offsetHeight + startingTop <=
						window.scrollY + 265
					) {
						setSocialTop(socialReference.current.offsetHeight - 145);
					} else {
						setSocialTop(0);
					}
				}
			});
		}
		if (!socialReference) setSocialReference(React.createRef());
	}, []);

	const contentfulNewsForLanguage = contentfulNewsByLanguage[language];

	// Fixed and English-only. It leaves out the URL's category segment, which has no page.
	const crumbs: Crumb[] = [
		{
			label: "Home",
			url: "/",
		},
		{
			label: "About Us",
			url: "/about-us",
		},
		{
			label: "News & Events",
			url: "/about-us/news-events",
		},
		{
			label: "News",
			url: "/about-us/news-events/news",
		},
	];
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary" vPadding="20px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb data={crumbs} type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						css={css`
							margin-top: 20px;
							margin-bottom: 35px;
							max-width: 760px;
							display: block;
						`}
						type="tertiary"
					>
						{contentfulNewsArticleForLanguage.title}
					</Typography>
					<Badge
						css={css`
							margin-bottom: 45px;
						`}
						type="tertiary"
						color={NewsBadgeColor(
							contentfulNewsArticleForLanguage.category as NewsCategories
						)}
					>
						{
							MultiLingualNewsCategories[language][
								contentfulNewsArticleForLanguage.categorySlug
							]
						}
					</Badge>
					<Media lessThan="xl">
						<SocialMedia orientation="horizontal" type="tertiary" />
						<Spacer />
						<Spacer />
					</Media>
					{contentfulNewsArticleForLanguage.subTitle &&
					contentfulNewsArticleForLanguage.subTitle.length > 0 ? (
						<Typography
							css={css`
								margin-bottom: 64px;
								max-width: 760px;
							`}
							type="tertiary"
							size="lg"
							variant="div"
						>
							{contentfulNewsArticleForLanguage.subTitle}
						</Typography>
					) : (
						<></>
					)}

					<Spacer
						css={css`
							height: 270px;
						`}
					/>
				</ContentSection>
				<ContentSection type="secondary">
					<Left
						ref={socialReference}
						css={css`
							background-color: ${theme.colors.primary.background};
						`}
					>
						<Media greaterThan="lg">
							<Social
								css={css`
									top: ${socialTop + "px"};
								`}
							>
								<SocialMedia orientation="vertical" type="tertiary" />
							</Social>
						</Media>
						<Typography variant="div" type="primary" size="md">
							{renderRichText(
								contentfulNewsArticleForLanguage.body.raw,
								"primary",
								contentfulNewsArticleForLanguage.body.references
							)}
						</Typography>
					</Left>
					<Right>
						<LatestNewsContainer
							css={css`
								background-color: ${theme.colors.primary.background};
							`}
						>
							<Typography
								variant="h4"
								type="primary"
								css={css`
									font-size: 20px;
									margin-top: 0px;
									margin-bottom: 20px;
								`}
							>
								{StaticContent[language].latestHeading}
							</Typography>
							<Divider
								css={css`
									margin-top: 0px;
									margin-bottom: 20px;
									color: ${theme.colors.primary.darkerGray};
								`}
							/>
							{contentfulNewsForLanguage.slice(0, 3).map((node, index: number) => {
								return (
									<Link
										to={
											"/about-us/news-events/news/" +
											node.categorySlug +
											"/" +
											node.slug
										}
										key={index}
									>
										<Typography
											variant="div"
											weight="bold"
											css={css`
												margin-bottom: 7px;
											`}
											color={theme.colors.primary.header}
											size="md"
										>
											{node.title ?? ""}
										</Typography>
										<Typography
											css={css`
												margin-bottom: 20px;
											`}
											variant="div"
											size="xs"
											color={theme.colors.primary.gray}
										>
											{FormatDate(node.publishDate)}
										</Typography>
										{index ===
										contentfulNewsForLanguage.slice(0, 3).length - 1 ? (
											<></>
										) : (
											<Divider
												css={css`
													margin-top: 0px;
													margin-bottom: 20px;
													color: ${theme.colors.primary.darkerGray};
												`}
											/>
										)}
									</Link>
								);
							})}
						</LatestNewsContainer>
						<StayTunedComponent
							parent={stayTunedParent}
							parentCategory={contentfulNewsArticleForLanguage.categorySlug}
							slim={true}
						/>
					</Right>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="28px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb data={crumbs} type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h3"
						css={css`
							margin-top: 17px;
							margin-bottom: 26px;
						`}
						type="tertiary"
					>
						{contentfulNewsArticleForLanguage.title}
					</Typography>
					<Badge
						css={css`
							margin-bottom: 43px;
						`}
						type="tertiary"
						color={NewsBadgeColor(
							contentfulNewsArticleForLanguage.category as NewsCategories
						)}
					>
						{
							MultiLingualNewsCategories[language][
								contentfulNewsArticleForLanguage.categorySlug
							]
						}
					</Badge>
					<SocialMedia orientation="horizontal" type="tertiary" />
				</ContentSection>
				<ContentSection type="primary" vPadding="32px">
					{contentfulNewsArticleForLanguage.subTitle &&
					contentfulNewsArticleForLanguage.subTitle.length > 0 ? (
						<Typography
							css={css`
								margin-bottom: 32px;
								font-size: 20px;
								margin-top: 0px;
							`}
							type="primary"
							size="xl"
							color={theme.colors.primary.header}
							variant="h3"
						>
							{contentfulNewsArticleForLanguage.subTitle}
						</Typography>
					) : (
						<></>
					)}
					<Typography
						variant="div"
						type="primary"
						css={css`
							margin-bottom: 22px;
							img {
								max-width: 100%;
							}
							p {
								margin-bottom: 20px;
							}
						`}
						size="md"
					>
						{renderRichText(
							contentfulNewsArticleForLanguage.body.raw,
							"primary",
							contentfulNewsArticleForLanguage.body.references
						)}
					</Typography>
					<SocialMedia
						color={theme.colors.primary.primaryBrand}
						type="primary"
						orientation="horizontal"
					/>
					<Divider
						css={css`
							margin-top: 8px;
							margin-bottom: 56px;
							color: ${theme.colors.primary.darkerGray};
						`}
					/>

					<Typography
						variant="h4"
						type="primary"
						css={css`
							font-size: 20px;
							margin-top: 0px;
							margin-bottom: 16px;
						`}
					>
						{StaticContent[language].latestHeading}
					</Typography>
					<Divider
						css={css`
							margin-top: 0px;
							margin-bottom: 16px;
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
					{contentfulNewsForLanguage.slice(0, 3).map((node, index: number) => {
						return (
							<Link
								to={
									"/about-us/news-events/news/" +
									node.categorySlug +
									"/" +
									node.slug
								}
								key={index}
							>
								<Typography
									variant="div"
									weight="bold"
									css={css`
										margin-bottom: 7px;
									`}
									color={theme.colors.primary.header}
									size="md"
								>
									{node.title ?? ""}
								</Typography>
								<Typography
									css={css`
										margin-bottom: 16px;
									`}
									variant="div"
									size="xs"
									color={theme.colors.primary.gray}
								>
									{FormatDate(node.publishDate)}
								</Typography>
								{index === contentfulNewsForLanguage.slice(0, 3).length - 1 ? (
									<></>
								) : (
									<Divider
										css={css`
											margin-top: 0px;
											margin-bottom: 16px;
											color: ${theme.colors.primary.darkerGray};
										`}
									/>
								)}
							</Link>
						);
					})}
				</ContentSection>
				<StayTunedComponent
					parent={stayTunedParent}
					parentCategory={contentfulNewsArticleForLanguage.categorySlug}
				/>
			</Media>
		</>
	);
};

export default NewsArticle;
